"use client"

import { Link } from "@inertiajs/react"

import { Avatar } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { tagLabel } from "@/lib/tags"
import { focalPointStyle } from "@/lib/utils"
import { collabPath } from "@/routes"
import type { Brief } from "@/types"

import { ProgressIndicator } from "./progress-indicator"

interface CollabCardProps {
  brief: Brief
}

const MAX_TAGS = 3
const MAX_AVATARS = 3

// Compact card for the collabs index. Cover image (3:2) with focal-point
// crop · top visible tags · title · subtitle · contributor avatar stack
// + suggestion/contributor counters. Everything ships server-resolved;
// the card never partitions contributors or counts suggestions
// locally.
export function CollabCard({ brief }: CollabCardProps) {
  const visibleTags = brief.taggings
    .filter((t) => t.is_visible)
    .slice(0, MAX_TAGS)
  const allContributors = [
    ...brief.featured_contributors,
    ...brief.track_contributors,
  ]
  const topContributors = allContributors.slice(0, MAX_AVATARS)

  return (
    <Link
      href={collabPath(brief.slug)}
      className="group border-secondary bg-primary hover:border-brand-300 flex flex-col overflow-hidden rounded-xl border transition-all hover:shadow-lg"
    >
      <div className="bg-surface-grouped relative aspect-[3/2] w-full overflow-hidden">
        {brief.cover_image_url ? (
          <img
            src={brief.cover_image_url}
            alt={brief.title}
            className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            style={focalPointStyle(brief.cover_image_focal_point)}
          />
        ) : (
          <div className="flex size-full items-center justify-center">
            <svg
              className="text-fg-quaternary size-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}

        {brief.progress.total_slots > 0 && (
          <div className="absolute inset-x-0 bottom-0 px-3 pb-2">
            <ProgressIndicator progress={brief.progress} compact />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        {visibleTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {visibleTags.map((tagging) => (
              <Badge key={tagging.id} size="sm" color="brand" type="pill-color">
                {tagLabel(tagging.tag)}
              </Badge>
            ))}
          </div>
        )}

        <h3 className="text-primary group-hover:text-brand-secondary font-semibold">
          {brief.title}
        </h3>

        {brief.subtitle && (
          <p className="text-tertiary line-clamp-2 text-sm">{brief.subtitle}</p>
        )}

        <div className="mt-auto flex items-center gap-4 pt-2">
          {topContributors.length > 0 && (
            <div className="flex items-center -space-x-1.5">
              {topContributors.map((contributor) => (
                <Avatar
                  key={contributor.id}
                  name={contributor.display_name}
                  avatarUrl={contributor.user?.avatar_url}
                  size="xs"
                />
              ))}
            </div>
          )}

          <span className="text-quaternary text-xs">
            {brief.suggestions_count}{" "}
            {brief.suggestions_count === 1 ? "suggestion" : "suggestions"}
            <span aria-hidden="true" className="px-1.5">
              ·
            </span>
            {brief.contributors_count}{" "}
            {brief.contributors_count === 1 ? "contributor" : "contributors"}
          </span>
        </div>
      </div>
    </Link>
  )
}
